import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import { type FC } from 'react';

import { type ActivityCostEstimateResponse } from '../../api/index.js';

interface PatientActivityCostEstimateProps {
  estimate: ActivityCostEstimateResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

function formatUsd(value: number, decimals = 2): string {
  return `$${value.toFixed(decimals)}`;
}

const PatientActivityCostEstimate: FC<PatientActivityCostEstimateProps> = ({ estimate, isLoading, isError }) => {
  if (isError) {
    return <Alert severity={'warning'}>Could not estimate the AI cost for these settings.</Alert>;
  }
  if (!estimate) {
    return isLoading ? <Skeleton variant={'rounded'} height={72} /> : null;
  }

  const source = estimate.costPerTickSource === 'empirical'
    ? 'averaged from this patient’s recent ticks'
    : 'from the pricing table, until this patient has enough ticks';
  const severity = estimate.overTarget ? 'warning' : 'info';

  return (
    <Alert severity={severity} icon={false}>
      <Stack direction={'row'} alignItems={'baseline'} spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Typography variant={'h6'} component={'span'}>
          {formatUsd(estimate.costPerMonthUsd)} / month
        </Typography>
        <Typography variant={'body2'} color={'text.secondary'} component={'span'}>
          estimated, against a {formatUsd(estimate.targetUsd, 0)} target
        </Typography>
      </Stack>
      <Box component={'span'} sx={{ display: 'block', typography: 'body2', mt: 0.5 }}>
        {estimate.ticksPerDay.toFixed(1)} ticks / day × {formatUsd(estimate.costPerTickUsd, 4)} per tick
        {' '}({source}) ≈ {formatUsd(estimate.costPerDayUsd)} / day.
      </Box>
      {estimate.overTarget ? (
        <Box component={'span'} sx={{ display: 'block', typography: 'body2', fontWeight: 600, mt: 0.5 }}>
          Over the per-patient budget. Lengthen the interval, shorten the clips or pick a cheaper model.
        </Box>
      ) : null}
      {estimate.ticksPerDay === 0 ? (
        <Box component={'span'} sx={{ display: 'block', typography: 'body2', mt: 0.5 }}>
          No upcoming check-in windows: add a CHECK_IN monitoring-hours rule for captures to run.
        </Box>
      ) : null}
    </Alert>
  );
};

export default PatientActivityCostEstimate;
